import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function NewPO() {
  const { t } = useLang();
  const [form, setForm] = useState({
    po_number: "",
    weaver_id: "",
    product_category: "HANDWOVEN",
    eta: "",
    po_date: new Date().toISOString().slice(0, 10),
  });
  const [lines, setLines] = useState([{ carpet_number: "", design_code: "", colour: "", size_text: "" }]);
  const [weavers, setWeavers] = useState([]);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => { api.get("/weavers").then((r) => setWeavers(r.data)); }, []);

  const submit = async () => {
    setErr(""); setOk("");
    if (!form.eta) { setErr(t("validation_eta_required")); return; }
    try {
      await api.post("/pos", {
        ...form,
        eta: new Date(form.eta).toISOString(),
        po_date: new Date(form.po_date).toISOString(),
        line_items: lines,
      });
      setOk("PO submitted — awaiting inspector assignment by supervisor.");
      setForm({ po_number: "", weaver_id: "", product_category: "HANDWOVEN", eta: "", po_date: new Date().toISOString().slice(0, 10) });
      setLines([{ carpet_number: "", design_code: "", colour: "", size_text: "" }]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="eyebrow">{t("nav_new_po")}</div>
      <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>{t("nav_new_po")}</h1>
      <div className="mt-2 text-xs text-[#7C8E9E]">PO will be issued after inspector assignment.</div>

      <div className="hag-surface p-4 mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label={t("po_number")} testId="po-number-input" value={form.po_number} onChange={(v) => setForm({ ...form, po_number: v })} placeholder="M3-371/26-27" />
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-[#4A627A]">{t("weaver_name")}</label>
          <select data-testid="po-weaver-select" value={form.weaver_id} onChange={(e) => setForm({ ...form, weaver_id: e.target.value })} className="mt-1 w-full h-12 px-2 border border-[#1B3A5C]/15 rounded-md">
            <option value="">— select —</option>
            {weavers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-[#4A627A]">Category</label>
          <select data-testid="po-cat-select" value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })} className="mt-1 w-full h-12 px-2 border border-[#1B3A5C]/15 rounded-md">
            <option>HANDWOVEN</option>
            <option>QUALITY_HANDKNOTTED</option>
          </select>
        </div>
        <F label={t("eta")} type="date" testId="po-eta-input" value={form.eta} onChange={(v) => setForm({ ...form, eta: v })} />
        <F label="PO Date" type="date" value={form.po_date} onChange={(v) => setForm({ ...form, po_date: v })} />
      </div>

      <h2 className="mt-6 font-bold" style={{ color: "var(--brand-navy)" }}>Carpet Line Items</h2>
      <div className="mt-2 space-y-2">
        {lines.map((ln, i) => (
          <div key={i} className="hag-surface p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
            <input data-testid={`line-carpet-${i}`} placeholder="Carpet number" value={ln.carpet_number} onChange={(e) => { const n = [...lines]; n[i].carpet_number = e.target.value; setLines(n); }} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
            <input placeholder="Design" value={ln.design_code} onChange={(e) => { const n = [...lines]; n[i].design_code = e.target.value; setLines(n); }} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
            <input placeholder="Colour" value={ln.colour} onChange={(e) => { const n = [...lines]; n[i].colour = e.target.value; setLines(n); }} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
            <input placeholder="Size (e.g. 12'-0''x15'-0'')" value={ln.size_text} onChange={(e) => { const n = [...lines]; n[i].size_text = e.target.value; setLines(n); }} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm col-span-2 md:col-span-1" />
            {form.product_category === "QUALITY_HANDKNOTTED" && (
              <input placeholder="Quality (10x60)" value={ln.quality_spec || ""} onChange={(e) => { const n = [...lines]; n[i].quality_spec = e.target.value; setLines(n); }} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
            )}
          </div>
        ))}
        <button data-testid="add-line-btn" onClick={() => setLines([...lines, { carpet_number: "", design_code: "", colour: "", size_text: "" }])} className="h-10 px-3 rounded-md text-sm border border-dashed border-[#1B3A5C]/25 w-full">+ Add line</button>
      </div>

      {err && <div data-testid="new-po-err" className="mt-4 text-sm p-2 rounded-md bg-red-50 border border-red-200 text-red-700">{err}</div>}
      {ok && <div className="mt-4 text-sm p-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">{ok}</div>}

      <button data-testid="submit-po-btn" onClick={submit} className="mt-4 h-12 px-6 rounded-md font-bold text-white" style={{ background: "var(--brand-navy)" }}>{t("submit")}</button>
    </div>
  );
}

function F({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#4A627A]">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full h-12 px-2 border border-[#1B3A5C]/15 rounded-md text-base" />
    </div>
  );
}
